import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { BarChart3, Check, ArrowRight, Shield, TrendingUp, Clock, Loader2, DollarSign } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompareProducts() {
  const { token } = useAuth();
  const { currentTemplate } = useTheme();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [investAmount, setInvestAmount] = useState(10000);

  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/placements/products`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProducts(res.data || []);
      } catch { toast.error('Erreur chargement produits'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [token]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 3) { toast.error('Maximum 3 produits'); return prev; }
      return [...prev, id];
    });
  };

  const compared = useMemo(() => products.filter(p => selected.includes(p.id)), [products, selected]);

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="compare-products-page">
        <div>
          <h1 className="text-2xl font-bold">Comparateur de placements</h1>
          <p className="text-muted-foreground text-sm mt-1">Sélectionnez jusqu&apos;à 3 produits pour les comparer côte à côte</p>
        </div>

        {/* Product selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(p => (
            <Card
              key={p.id}
              className={`cursor-pointer transition-all duration-200 ${selected.includes(p.id) ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/40'}`}
              onClick={() => toggleSelect(p.id)}
              data-testid={`compare-select-${p.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-primary" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold">{p.name}</h3>
                      <p className="text-xs text-muted-foreground">{p.category?.title || p.subcategory}</p>
                    </div>
                  </div>
                  <Checkbox checked={selected.includes(p.id)} />
                </div>
                <div className="flex items-center gap-4 mt-3 text-sm">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-0">{p.rentability_rate}%</Badge>
                  <span className="text-muted-foreground">{p.duration_months} mois</span>
                  <span className="text-muted-foreground">Min. {p.min_investment?.toLocaleString('fr-FR')} €</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Comparison table */}
        {compared.length >= 2 && (
          <Card className="bg-card/80 backdrop-blur border-border/50" data-testid="comparison-table">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: primaryColor }} />
                  Comparaison
                </CardTitle>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={investAmount}
                    onChange={(e) => setInvestAmount(Number(e.target.value) || 0)}
                    className="w-36 text-right"
                    placeholder="Montant"
                    data-testid="compare-invest-amount"
                  />
                  <span className="text-sm text-muted-foreground">€</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-muted-foreground font-medium w-48">Critère</th>
                      {compared.map(p => (
                        <th key={p.id} className="text-center py-3 px-4 font-semibold" style={{ color: primaryColor }}>
                          {p.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Rentabilité', key: 'rentability_rate', format: v => <Badge className="bg-emerald-500/20 text-emerald-400 border-0">{v}% / {compared[0]?.rentability_period}</Badge> },
                      { label: 'Durée', key: 'duration_months', format: v => `${v} mois` },
                      { label: 'Invest. minimum', key: 'min_investment', format: v => `${v?.toLocaleString('fr-FR')} €` },
                      { label: 'Invest. maximum', key: 'max_investment', format: v => `${v?.toLocaleString('fr-FR')} €` },
                      { label: 'Intérêts composés', key: 'interest_compound', format: v => v ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <span className="text-muted-foreground">Non</span> },
                      { label: 'Reconduction auto', key: 'auto_renewal', format: v => v ? 'Oui' : 'Non' },
                      { label: 'Fonds bloqués', key: 'capitalize_funds', format: v => v ? 'Oui' : 'Disponibles' },
                      { label: 'Indice de confiance', key: 'confidence_index', format: v => `${v}/100` },
                      { label: `Intérêt par période (${investAmount.toLocaleString('fr-FR')}€)`, key: null, format: (_, p) => {
                        const pm = { 'Mensuel': 1, 'Trimestriel': 3, 'Semestriel': 6, 'Annuel': 12 };
                        const base = pm[p.rentability_period] || 1;
                        const periodRate = (p.rentability_rate / 100);
                        const interest = investAmount * periodRate;
                        return <span className="text-emerald-400 font-semibold">+{interest.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € / {p.rentability_period?.toLowerCase()}</span>;
                      }},
                      { label: `Rendement total (${investAmount.toLocaleString('fr-FR')}€)`, key: null, format: (_, p) => {
                        const pm = { 'Mensuel': 1, 'Trimestriel': 3, 'Semestriel': 6, 'Annuel': 12 };
                        const base = pm[p.rentability_period] || 1;
                        const periods = p.duration_months / base;
                        const periodRate = p.rentability_rate / 100;
                        let total;
                        if (p.interest_compound) {
                          total = investAmount * Math.pow(1 + periodRate, periods) - investAmount;
                        } else {
                          total = investAmount * periodRate * periods;
                        }
                        return <span className="text-emerald-400 font-bold text-base">+{total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>;
                      }},
                      { label: `Capital final (${investAmount.toLocaleString('fr-FR')}€)`, key: null, format: (_, p) => {
                        const pm = { 'Mensuel': 1, 'Trimestriel': 3, 'Semestriel': 6, 'Annuel': 12 };
                        const base = pm[p.rentability_period] || 1;
                        const periods = p.duration_months / base;
                        const periodRate = p.rentability_rate / 100;
                        let finalCapital;
                        if (p.interest_compound) {
                          finalCapital = investAmount * Math.pow(1 + periodRate, periods);
                        } else {
                          finalCapital = investAmount + (investAmount * periodRate * periods);
                        }
                        return <span className="font-bold text-base">{finalCapital.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>;
                      }},
                    ].map((row, idx) => (
                      <tr key={idx} className="border-b border-border/30 hover:bg-muted/20">
                        <td className="py-3 px-4 text-muted-foreground font-medium">{row.label}</td>
                        {compared.map(p => (
                          <td key={p.id} className="py-3 px-4 text-center">
                            {row.key !== null ? row.format(p[row.key], p) : row.format(null, p)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* CTA */}
              <div className="flex gap-4 mt-6 justify-center">
                {compared.map(p => (
                  <Link key={p.id} to={`/client/placements/product/${p.id}`}>
                    <Button variant="outline" className="gap-2" data-testid={`compare-cta-${p.id}`}>
                      {p.name} <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {compared.length < 2 && selected.length > 0 && (
          <div className="text-center py-8 text-muted-foreground">
            Sélectionnez au moins 2 produits pour comparer
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
